import { useState, useEffect, useCallback, useRef } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
  timestamp: number;
}

interface UseVersionCheckReturn {
  hasUpdate: boolean;
  currentVersion: string | null;
  latestVersion: string | null;
  isChecking: boolean;
  refresh: () => void;
  dismissUpdate: () => void;
  checkNow: () => Promise<void>;
}

// Intervalo de verificação: 2 minutos
const CHECK_INTERVAL = 2 * 60 * 1000;

// Tempo mínimo entre checks manuais: 10 segundos
const MIN_CHECK_INTERVAL = 10 * 1000;

export function useVersionCheck(): UseVersionCheckReturn {
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const lastCheckRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      // Cache-busting: adiciona timestamp para evitar cache
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.debug('[VersionCheck] Failed to fetch version.json:', response.status);
        return null;
      }
      
      return await response.json();
    } catch (error) {
      // Falha silenciosa - não bloqueia o app
      console.debug('[VersionCheck] Error fetching version:', error);
      return null;
    }
  }, []);

  const checkVersion = useCallback(async () => {
    const now = Date.now();
    
    // Evita checks muito frequentes
    if (now - lastCheckRef.current < MIN_CHECK_INTERVAL) {
      return;
    }
    
    lastCheckRef.current = now;
    setIsChecking(true);
    
    try {
      const versionInfo = await fetchVersion();
      
      if (!versionInfo) {
        setIsChecking(false);
        return;
      }
      
      // Primeira execução: armazena versão atual
      if (!currentVersion) {
        setCurrentVersion(versionInfo.version);
        setLatestVersion(versionInfo.version);
        console.debug('[VersionCheck] Initial version:', versionInfo.version);
      } else {
        setLatestVersion(versionInfo.version);
        
        // Detecta nova versão
        if (versionInfo.version !== currentVersion && !dismissed) {
          console.log('[VersionCheck] Nova versão detectada:', versionInfo.version);
          setHasUpdate(true);
        }
      }
    } finally {
      setIsChecking(false);
    }
  }, [currentVersion, dismissed, fetchVersion]);

  const refresh = useCallback(() => {
    // Limpa qualquer estado local que possa interferir
    sessionStorage.clear();
    
    // Força reload completo
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    setDismissed(true);
    setHasUpdate(false);
    
    // Re-habilita após 30 minutos
    setTimeout(() => {
      setDismissed(false);
    }, 30 * 60 * 1000);
  }, []);

  const checkNow = useCallback(async () => {
    lastCheckRef.current = 0; // Reset para permitir check imediato
    await checkVersion();
  }, [checkVersion]);

  // Verificação inicial + polling
  useEffect(() => {
    // Check inicial após 5 segundos (dá tempo do app carregar)
    const initialTimeout = setTimeout(() => {
      checkVersion();
    }, 5000);

    // Polling a cada 2 minutos
    intervalRef.current = setInterval(() => {
      // Só verifica se a janela está visível
      if (!document.hidden) {
        checkVersion();
      }
    }, CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkVersion]);

  // Verifica quando a janela volta ao foco (após estar oculta por mais de 10 min)
  useEffect(() => {
    let hiddenAt: number | null = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt) {
        const hiddenDuration = Date.now() - hiddenAt;
        // Se ficou oculto por mais de 10 minutos, verifica
        if (hiddenDuration > 10 * 60 * 1000) {
          checkVersion();
        }
        hiddenAt = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return {
    hasUpdate,
    currentVersion,
    latestVersion,
    isChecking,
    refresh,
    dismissUpdate,
    checkNow
  };
}
